import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context.js';
import {
  DEMO_SCENARIOS_PAGED,
  DEMO_WORKFRONT_OBJECTS,
  getDemoScenarioDetail,
  toEnvelope,
  useDemoMode,
  useDemoOrLive,
} from '../demo/index.js';

/**
 * Scenario hooks. Each maps to a `/v1/scenarios/*` route on app-api,
 * which forwards to the engine. Response shapes are loose-typed
 * `Record<string, unknown>` until the engine response contracts
 * land in `packages/app-types`.
 */

type Row = Record<string, unknown>;

/**
 * Engine response envelopes are mixed: list endpoints wrap rows in
 * `{ data, total, page, pageSize }`, detail/filters/custom-data
 * endpoints return the payload directly, and some (kpi) wrap a
 * single row in `{ data: row }`. Hooks below unwrap to match what
 * the SPA actually consumes.
 */
interface PagedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** The engine's largest scenario-list page. */
const LIST_PAGE_SIZE = 200;

export function useScenariosList(): {
  data?: Row[];
  total?: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['scenarios', 'list', session?.licenseId ?? 'anon'],
    // Pull the full per-license set — every consumer filters and
    // counts client-side. `pageSize=200` matches the engine's MAX (see
    // `packages/types/src/scenario-list.ts`); a license past one page
    // gets the rest fetched in parallel, so no scenario is dropped.
    queryFn: async () => {
      const first = await api.get<PagedResponse<Row>>(`/v1/scenarios?pageSize=${LIST_PAGE_SIZE}`);
      const pageCount = Math.ceil(first.total / LIST_PAGE_SIZE);
      if (pageCount <= 1) return first;
      const rest = await Promise.all(
        Array.from({ length: pageCount - 1 }, (_, i) =>
          api.get<PagedResponse<Row>>(`/v1/scenarios?pageSize=${LIST_PAGE_SIZE}&page=${i + 2}`),
        ),
      );
      return { ...first, data: first.data.concat(...rest.map((p) => p.data)) };
    },
    enabled: !!session,
    staleTime: 30_000,
  });
  const env = useDemoOrLive(DEMO_SCENARIOS_PAGED, toEnvelope(q));
  return {
    data: env.data?.data,
    total: env.data?.total,
    isLoading: env.isLoading,
    isFetching: env.isFetching ?? false,
    error: env.error,
    refetch: env.refetch ?? (() => undefined),
  };
}

export function useScenarioDetail(fusionId: string | undefined): {
  data?: Row;
  isLoading: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const { isDemoMode } = useDemoMode();
  const q = useQuery({
    queryKey: ['scenarios', 'detail', session?.licenseId ?? 'anon', fusionId ?? ''],
    queryFn: () => api.get<Row>(`/v1/scenarios/${encodeURIComponent(fusionId ?? '')}`),
    enabled: !!session && !!fusionId && !isDemoMode,
    staleTime: 30_000,
  });
  // Detail is keyed on the path param — read the fixture by the same
  // key so deep-linking to a demo scenario works (and unknown keys
  // fall back to the first demo row rather than 404ing).
  if (isDemoMode) {
    return {
      data: getDemoScenarioDetail(fusionId),
      isLoading: false,
      error: null,
    };
  }
  return { data: q.data, isLoading: q.isLoading, error: (q.error as Error) ?? null };
}

/**
 * Fusion blueprint version-list for one scenario. Engine proxies Fusion
 * API v3 `GET /scenarios/{id}/versions` and returns
 * `{ blueprints: [{ id, version, created, comment }] }`, newest first;
 * `id` is the v3 version number. Used by the compare-code dialog to
 * populate the version dropdown.
 */
export interface ScenarioBlueprintVersion {
  id?: number;
  version?: number;
  created?: string;
  [extra: string]: unknown;
}

export function useScenarioBlueprints(fusionId: string | undefined): {
  data?: ReadonlyArray<ScenarioBlueprintVersion>;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['scenarios', 'blueprints', session?.licenseId ?? 'anon', fusionId ?? ''],
    queryFn: () =>
      api
        .get<{
          blueprints: ReadonlyArray<ScenarioBlueprintVersion>;
        }>(`/v1/scenarios/${encodeURIComponent(fusionId ?? '')}/blueprints`)
        .then((r) => r.blueprints),
    enabled: !!session && !!fusionId,
    staleTime: 60_000,
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: (q.error as Error) ?? null,
  };
}

/**
 * One blueprint version's full JSON payload. Engine proxies Fusion API
 * v3 `GET /scenarios/{id}/versions/{version}` (`blueprintId` is that
 * version number) and returns `{ blueprint }`, the blueprint object
 * itself. Cached briefly — once an operator picks a version the
 * rendered diff is stable.
 */
export function useScenarioBlueprintDetails(
  fusionId: string | undefined,
  blueprintId: number | undefined,
): {
  data?: unknown;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: [
      'scenarios',
      'blueprint-details',
      session?.licenseId ?? 'anon',
      fusionId ?? '',
      blueprintId ?? '',
    ],
    queryFn: () =>
      api
        .get<{
          blueprint: unknown;
        }>(
          `/v1/scenarios/${encodeURIComponent(fusionId ?? '')}/blueprint?blueprintId=${encodeURIComponent(String(blueprintId ?? ''))}`,
        )
        .then((r) => r.blueprint),
    enabled: !!session && !!fusionId && blueprintId !== undefined && blueprintId !== null,
    staleTime: 60_000,
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: (q.error as Error) ?? null,
  };
}

/**
 * Event-subscription compare. Engine resolves the scenario's
 * webhook → Fusion → Workfront chain and returns the list of
 * subscriptions for the underlying webhook URL. One scenario per
 * call; the dialog fires two of these. Mirrors the engine's allowlist
 * (`EventSubscriptionSchema`): no other field is returned.
 */
export interface ScenarioEventSubscription {
  id?: string;
  objCode?: string;
  eventType?: string;
  filterConnector?: string;
  status?: string;
  filters?: ReadonlyArray<{
    fieldName?: string;
    fieldValue?: unknown;
    state?: string;
    comparison?: string;
  }>;
}

export function useScenarioEventSubscriptions(
  fusionId: string | undefined,
  enabled = true,
): {
  data?: { subscriptions: ReadonlyArray<ScenarioEventSubscription> };
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['scenarios', 'event-subscriptions', session?.licenseId ?? 'anon', fusionId ?? ''],
    queryFn: () =>
      api.get<{
        subscriptions: ReadonlyArray<ScenarioEventSubscription>;
      }>(`/v1/scenarios/${encodeURIComponent(fusionId ?? '')}/event-subscriptions`),
    enabled: enabled && !!session && !!fusionId,
    staleTime: 60_000,
    retry: false,
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: (q.error as Error) ?? null,
  };
}

export function useScenarioFilters(): {
  data?: Row;
  isLoading: boolean;
  error: Error | null;
} {
  // /scenarios/filters returns `{ data }` envelope per engine.
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['scenarios', 'filters', session?.licenseId ?? 'anon'],
    queryFn: () => api.get<{ data: Row }>('/v1/scenarios/filters').then((r) => r.data),
    enabled: !!session,
    staleTime: 5 * 60_000,
  });
  return { data: q.data, isLoading: q.isLoading, error: (q.error as Error) ?? null };
}

export interface WorkfrontObjectItem {
  id: number;
  workfrontId: string;
  fusionModuleId: string;
  fusionModuleType: string;
  fusionModuleName: string;
  fusionScenarioId: string;
  workfrontType: string | null;
  workfrontLink: string | null;
  workfrontName: string | null;
  workfrontTypeLabel: string | null;
}

export interface CustomFieldsItem {
  fusionId: string;
  customFieldsData: unknown;
}

/**
 * One row per (planning_object × fusion_module) pair, as returned by
 * `GET /engine/v1/planning/fusion-usage` when called without a
 * `workspaceId` query string — see
 * [packages/db/src/repositories/planning-object.ts findFusionUsageByLicense].
 * `fusionScenarioId` is the join key the SPA pivots on.
 */
export interface PlanningObjectItem {
  planningObjectType: string;
  planningObjectId: string;
  workspaceId: string | null;
  recordTypeId: string | null;
  recordTypeName: string | null;
  objectName: string | null;
  fusionModuleId: string | null;
  fusionModuleName: string | null;
  fusionModuleType: string | null;
  fusionScenarioId: number | null;
  fusionScenarioName: string | null;
  scenarioId: number | null;
  fusionTeamId: number | null;
}

/**
 * Workfront-object rows for every scenario in the current license.
 * One license-wide fetch — the legacy fired this once and grouped
 * client-side; we do the same. Disabled on `business` role since the
 * Business Executive view doesn't render the sub-tables (matches
 * legacy gating where `showCollapseColumn` defaults to `false`).
 */
export function useScenarioWorkfrontObjects(enabled: boolean): {
  byScenario: Map<string, ReadonlyArray<WorkfrontObjectItem>>;
  isLoading: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const { isDemoMode } = useDemoMode();
  const q = useQuery({
    queryKey: ['scenarios', 'workfront-objects', session?.licenseId ?? 'anon'],
    queryFn: () =>
      api
        .get<{ data: WorkfrontObjectItem[] }>('/v1/scenarios/workfront-objects-data')
        .then((r) => r.data ?? []),
    enabled: !!session && enabled && !isDemoMode,
    staleTime: 60_000,
  });
  // Demo mode swaps to the flat license-wide fixture derived from
  // `DEMO_SCENARIOS[*].workfrontObjects`. Pre-fix the demo branch
  // never hit this endpoint at all — the expand-row + the Workfront-
  // Objects-(All) tab rendered empty even though scenario detail
  // already carried per-scenario objects. This restores parity.
  const rows = isDemoMode
    ? (DEMO_WORKFRONT_OBJECTS as ReadonlyArray<WorkfrontObjectItem>)
    : (q.data ?? []);
  const byScenario = useMemo(() => groupByFusionScenarioId(rows), [rows]);
  return {
    byScenario,
    isLoading: isDemoMode ? false : q.isLoading,
    error: isDemoMode ? null : ((q.error as Error) ?? null),
  };
}

/**
 * Planning-object rows for every scenario in the current license. One
 * license-wide fetch matches the workfront-objects + custom-fields
 * pattern above — the SPA's expand-panel pivots client-side by
 * `fusionScenarioId`. Demo mode returns an empty map (no demo
 * planning fixtures yet — planning is dev-tenant-only today and the
 * blueprint extraction needs real Maestro IDs).
 */
export function useScenarioPlanningObjects(enabled: boolean): {
  byScenario: Map<string, ReadonlyArray<PlanningObjectItem>>;
  isLoading: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const { isDemoMode } = useDemoMode();
  const q = useQuery({
    queryKey: ['scenarios', 'planning-objects', session?.licenseId ?? 'anon'],
    queryFn: () =>
      api
        .get<{ data: PlanningObjectItem[] }>('/v1/scenarios/planning-objects-data')
        .then((r) => r.data ?? []),
    enabled: !!session && enabled && !isDemoMode,
    staleTime: 60_000,
  });
  const rows = isDemoMode ? ([] as ReadonlyArray<PlanningObjectItem>) : (q.data ?? []);
  const byScenario = useMemo(() => groupPlanningByFusionScenarioId(rows), [rows]);
  return {
    byScenario,
    isLoading: isDemoMode ? false : q.isLoading,
    error: isDemoMode ? null : ((q.error as Error) ?? null),
  };
}

export function useScenarioCustomFields(enabled: boolean): {
  byScenario: Map<string, CustomFieldsItem>;
  isLoading: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['scenarios', 'custom-fields', session?.licenseId ?? 'anon'],
    queryFn: () =>
      api
        .get<{ data: CustomFieldsItem[] }>('/v1/scenarios/custom-fields-data')
        .then((r) => r.data ?? []),
    enabled: !!session && enabled,
    staleTime: 60_000,
  });
  const byScenario = useMemo(() => {
    const map = new Map<string, CustomFieldsItem>();
    for (const item of q.data ?? []) {
      if (item.fusionId) map.set(item.fusionId, item);
    }
    return map;
  }, [q.data]);
  return {
    byScenario,
    isLoading: q.isLoading,
    error: (q.error as Error) ?? null,
  };
}

function groupByFusionScenarioId(
  items: ReadonlyArray<WorkfrontObjectItem>,
): Map<string, ReadonlyArray<WorkfrontObjectItem>> {
  const map = new Map<string, WorkfrontObjectItem[]>();
  for (const it of items) {
    const arr = map.get(it.fusionScenarioId);
    if (arr) arr.push(it);
    else map.set(it.fusionScenarioId, [it]);
  }
  return map;
}

function groupPlanningByFusionScenarioId(
  items: ReadonlyArray<PlanningObjectItem>,
): Map<string, ReadonlyArray<PlanningObjectItem>> {
  // `fusionScenarioId` arrives as a number from the engine's
  // ScenarioPlanningObjectItem; key on the string representation so
  // it matches the `ScenarioRowData.fusionId` lookup the panel uses.
  const map = new Map<string, PlanningObjectItem[]>();
  for (const it of items) {
    if (it.fusionScenarioId === null) continue;
    const key = String(it.fusionScenarioId);
    const arr = map.get(key);
    if (arr) arr.push(it);
    else map.set(key, [it]);
  }
  return map;
}

/**
 * Body of `PUT /v1/scenarios/{fusionId}`: the fields the engine's
 * strict `UpdateScenarioSchema` accepts (`@wf-coe/types`
 * `scenario-update.ts`). Any other field is a 400; the description
 * lives in custom data (`useUpdateScenarioCustomData`).
 */
export interface ScenarioUpdatePatch {
  hourlyRate?: string | null;
  hoursSaved?: string | null;
  tagIds?: number[];
}

export function useUpdateScenario(fusionId: string | undefined): {
  update: (patch: ScenarioUpdatePatch) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (patch: ScenarioUpdatePatch) =>
      api.put<unknown>(`/v1/scenarios/${encodeURIComponent(fusionId ?? '')}`, patch),
    onSuccess: (_data, patch) => {
      void qc.invalidateQueries({
        queryKey: ['scenarios', 'detail', session?.licenseId ?? 'anon', fusionId ?? ''],
      });
      void qc.invalidateQueries({ queryKey: ['scenarios', 'list'] });
      // Targeted success message — most callers only patch one field
      // at a time (tags from the selector). The toast hint surfaces
      // which.
      const fields = Object.keys(patch ?? {});
      if (fields.length === 1) {
        toast.success(`Scenario ${fields[0]} updated`);
      } else {
        toast.success('Scenario updated');
      }
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });
  return {
    update: (patch) => m.mutateAsync(patch),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useUpdateScenarioCustomData(fusionId: string | undefined): {
  save: (customData: Record<string, unknown>) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (customData: Record<string, unknown>) =>
      api.put<unknown>(
        `/v1/scenarios/${encodeURIComponent(fusionId ?? '')}/custom-data`,
        customData,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['scenarios', 'detail', session?.licenseId ?? 'anon', fusionId ?? ''],
      });
    },
  });
  return {
    save: (data) => m.mutateAsync(data),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}
